import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Briefcase,
  FileText,
  CreditCard,
  LifeBuoy,
  RefreshCw,
  PlusCircle,
  Clock,
  CheckCircle2,
  AlertCircle,
  Download,
  Calendar,
  DollarSign,
  ArrowRight,
  Shield,
  MessageSquare,
  Building2,
  ExternalLink,
  GraduationCap,
  Layers,
  Upload,
  Eye,
  HelpCircle,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import type {
  Project,
  Quotation,
  Invoice,
  ServiceSubscription,
  SupportTicket,
  Payment,
  Enrollment,
} from '../../types/database';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { generateInvoicePDF, generateQuotationPDF } from '../../lib/pdfGenerator';
import ManualReceiptModal from '../../components/portal/ManualReceiptModal';
import TicketChatModal from '../../components/support/TicketChatModal';

export default function ClientPortalPage() {
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<
    'services' | 'projects' | 'quotes' | 'invoices' | 'payments' | 'courses' | 'support'
  >('services');

  // Data States
  const [requestedServices, setRequestedServices] = useState<any[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [subscriptions, setSubscriptions] = useState<ServiceSubscription[]>([]);
  const [payments, setPayments] = useState<any[]>([]);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // New ticket modal
  const [showNewTicketModal, setShowNewTicketModal] = useState(false);
  const [ticketSubject, setTicketSubject] = useState('');
  const [ticketDescription, setTicketDescription] = useState('');
  const [ticketPriority, setTicketPriority] = useState<'Low' | 'Medium' | 'High' | 'Urgent'>('Medium');
  const [ticketSubmitting, setTicketSubmitting] = useState(false);

  // Revision request modal
  const [revisionProject, setRevisionProject] = useState<Project | null>(null);
  const [revisionNotes, setRevisionNotes] = useState('');
  const [submittingRevision, setSubmittingRevision] = useState(false);

  // Manual Receipt Submission Modal
  const [selectedInvoiceForReceipt, setSelectedInvoiceForReceipt] = useState<Invoice | null>(null);
  const [selectedPaymentForUpdate, setSelectedPaymentForUpdate] = useState<Payment | null>(null);
  const [showReceiptModal, setShowReceiptModal] = useState(false);

  // Support ticket chat modal
  const [selectedTicketForChat, setSelectedTicketForChat] = useState<SupportTicket | null>(null);
  const [showChatModal, setShowChatModal] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: '/portal' } } });
      return;
    }
    fetchClientData();
  }, [user]);

  const fetchClientData = async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const results = await Promise.allSettled([
        // 0. Requested Services
        user?.email
          ? supabase
              .from('leads')
              .select('*, service:services(*)')
              .eq('email', user.email)
              .order('created_at', { ascending: false })
          : Promise.resolve({ data: [] }),
        // 1. Projects
        supabase
          .from('projects')
          .select('*, service:services(*), milestones:project_milestones(*), tasks:project_tasks(*)')
          .eq('client_id', user!.id)
          .order('created_at', { ascending: false }),
        // 2. Quotations
        supabase
          .from('quotations')
          .select('*, items:quotation_items(*)')
          .eq('client_id', user!.id)
          .order('created_at', { ascending: false }),
        // 3. Invoices
        supabase
          .from('invoices')
          .select('*, items:invoice_items(*), project:projects(*)')
          .eq('client_id', user!.id)
          .order('created_at', { ascending: false }),
        // 4. Payments
        supabase
          .from('payments')
          .select('*, invoice:invoices(*)')
          .eq('client_id', user!.id)
          .order('created_at', { ascending: false }),
        // 5. Subscriptions
        supabase
          .from('service_subscriptions')
          .select('*, service:services(*)')
          .eq('client_id', user!.id)
          .order('next_renewal_date'),
        // 6. Courses / Enrollments
        supabase
          .from('enrollments')
          .select('*, course:courses(*)')
          .eq('user_id', user!.id)
          .order('enrolled_at', { ascending: false }),
        // 7. Support Tickets
        supabase
          .from('support_tickets')
          .select('*')
          .eq('client_id', user!.id)
          .order('created_at', { ascending: false }),
      ]);

      if (results[0].status === 'fulfilled' && (results[0].value as any)?.data) {
        setRequestedServices((results[0].value as any).data);
      }
      if (results[1].status === 'fulfilled' && (results[1].value as any)?.data) {
        setProjects((results[1].value as any).data as Project[]);
      }
      if (results[2].status === 'fulfilled' && (results[2].value as any)?.data) {
        setQuotations((results[2].value as any).data as Quotation[]);
      }
      if (results[3].status === 'fulfilled' && (results[3].value as any)?.data) {
        setInvoices((results[3].value as any).data as Invoice[]);
      }
      if (results[4].status === 'fulfilled' && (results[4].value as any)?.data) {
        setPayments((results[4].value as any).data);
      }
      if (results[5].status === 'fulfilled' && (results[5].value as any)?.data) {
        setSubscriptions((results[5].value as any).data as ServiceSubscription[]);
      }
      if (results[6].status === 'fulfilled' && (results[6].value as any)?.data) {
        setEnrollments((results[6].value as any).data);
      }
      if (results[7].status === 'fulfilled' && (results[7].value as any)?.data) {
        setTickets((results[7].value as any).data as SupportTicket[]);
      }
    } catch (err) {
      console.error('[CLIENT PORTAL] Error fetching client data:', err);
      setFetchError('Some data could not be refreshed.');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptQuote = async (quoteId: string) => {
    try {
      await supabase
        .from('quotations')
        .update({ status: 'Accepted', updated_at: new Date().toISOString() })
        .eq('id', quoteId);
      await fetchClientData();
    } catch (err) {
      console.error('Error accepting quote:', err);
    }
  };

  const handleRejectQuote = async (quoteId: string) => {
    try {
      await supabase
        .from('quotations')
        .update({ status: 'Rejected', updated_at: new Date().toISOString() })
        .eq('id', quoteId);
      await fetchClientData();
    } catch (err) {
      console.error('Error rejecting quote:', err);
    }
  };

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ticketSubject || !ticketDescription || !user) return;

    setTicketSubmitting(true);
    try {
      const ticketNum = `TCK-${Date.now().toString().slice(-6)}`;
      const { data, error } = await supabase
        .from('support_tickets')
        .insert({
          ticket_number: ticketNum,
          client_id: user.id,
          subject: ticketSubject,
          description: ticketDescription,
          priority: ticketPriority,
          status: 'Open',
        })
        .select()
        .single();

      if (!error && data) {
        await supabase.from('support_messages').insert({
          ticket_id: data.id,
          sender_id: user.id,
          message: ticketDescription,
          is_internal_note: false,
        });

        setTicketSubject('');
        setTicketDescription('');
        setShowNewTicketModal(false);
        await fetchClientData();
      }
    } catch (err) {
      console.error('Ticket error:', err);
    } finally {
      setTicketSubmitting(false);
    }
  };

  const handleSubmitRevision = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!revisionProject || !revisionNotes.trim() || !user) return;

    setSubmittingRevision(true);
    try {
      // Create project comment / revision note
      await supabase.from('project_comments').insert({
        project_id: revisionProject.id,
        user_id: user.id,
        message: `Client Revision Request: ${revisionNotes.trim()}`,
        is_internal: false,
      });

      // Update project status to Revision Requested / Review
      await supabase
        .from('projects')
        .update({ status: 'Review', updated_at: new Date().toISOString() })
        .eq('id', revisionProject.id);

      setRevisionProject(null);
      setRevisionNotes('');
      await fetchClientData();
    } catch (err) {
      console.error('Revision error:', err);
    } finally {
      setSubmittingRevision(false);
    }
  };

  const openReceiptSubmission = (inv?: Invoice, pay?: Payment) => {
    setSelectedInvoiceForReceipt(inv || null);
    setSelectedPaymentForUpdate(pay || null);
    setShowReceiptModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col pt-20">
      <Navbar />

      <main className="flex-1 max-w-7xl w-full mx-auto px-4 lg:px-8 py-10">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className="px-3 py-1 bg-clairBlue-50 text-clairBlue-600 rounded-full text-xs font-bold uppercase tracking-wider">
                Client Operations Hub
              </span>
              {profile?.organization_name && (
                <span className="flex items-center gap-1 text-xs text-gray-500 font-semibold">
                  <Building2 className="w-3.5 h-3.5 text-gray-400" />
                  {profile.organization_name}
                </span>
              )}
            </div>
            <h1 className="text-3xl font-display font-bold text-darkBlue-900">
              Welcome back, {profile?.full_name || 'Client'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Manage your commissioned digital services, project deliverables, quotations, manual payment proofs, and learning courses.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/portal/request-service"
              className="flex items-center gap-2 px-5 py-2.5 bg-darkBlue-900 hover:bg-darkBlue-800 text-white rounded-xl text-xs font-bold transition-all shadow-md hover:scale-105"
            >
              <PlusCircle className="w-4 h-4" />
              Commission Service
            </Link>
            <button
              onClick={() => openReceiptSubmission()}
              className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-sm"
            >
              <Upload className="w-4 h-4" />
              <span>Submit Payment Proof</span>
            </button>
          </div>
        </div>

        {/* Navigation Tabs (Part 3 Specification) */}
        <div className="flex items-center gap-2 border-b border-gray-200 mb-8 overflow-x-auto pb-px scrollbar-none">
          {[
            { id: 'services', label: 'My Services', count: requestedServices.length + subscriptions.length, icon: Layers },
            { id: 'projects', label: 'My Projects', count: projects.length, icon: Briefcase },
            { id: 'quotes', label: 'My Quotations', count: quotations.length, icon: FileText },
            { id: 'invoices', label: 'My Invoices', count: invoices.length, icon: CreditCard },
            { id: 'payments', label: 'My Payments', count: payments.length, icon: DollarSign },
            { id: 'courses', label: 'My Courses', count: enrollments.length, icon: GraduationCap },
            { id: 'support', label: 'My Support', count: tickets.length, icon: LifeBuoy },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 font-bold text-xs sm:text-sm transition-all whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'border-darkBlue-900 text-darkBlue-900'
                    : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <Icon className={`w-4 h-4 ${isActive ? 'text-clairBlue-600' : 'text-gray-400'}`} />
                <span>{tab.label}</span>
                {tab.count > 0 && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-[11px] ${
                      isActive ? 'bg-darkBlue-900 text-white' : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Tab Contents */}
        <div>
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="w-8 h-8 border-4 border-clairBlue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : activeTab === 'services' ? (
            /* Tab 1: My Services */
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-darkBlue-900">Requested & Active Services</h3>
                  <p className="text-xs text-gray-500">Service inquiries, active retainer plans, and recurring agreements</p>
                </div>
                <Link
                  to="/portal/request-service"
                  className="px-4 py-2 bg-darkBlue-900 text-white rounded-xl text-xs font-bold"
                >
                  + Request Another Service
                </Link>
              </div>

              {requestedServices.length === 0 && subscriptions.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                  <Layers className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h4 className="text-base font-bold text-gray-800">No services requested yet</h4>
                  <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">
                    Explore our official digital presence, business systems, or AI solutions catalog.
                  </p>
                  <Link
                    to="/services"
                    className="px-5 py-2.5 bg-darkBlue-900 text-white rounded-xl text-xs font-bold"
                  >
                    Browse Services Catalog
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {requestedServices.map((req) => (
                    <div
                      key={req.id}
                      className="bg-white rounded-3xl p-6 border border-gray-200/80 shadow-sm flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] font-bold text-clairBlue-600 bg-clairBlue-50 px-2.5 py-0.5 rounded-full">
                            Service Request
                          </span>
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                              req.status === 'Won'
                                ? 'bg-emerald-50 text-emerald-700'
                                : req.status === 'Quotation Sent'
                                ? 'bg-blue-50 text-blue-700'
                                : 'bg-amber-50 text-amber-700'
                            }`}
                          >
                            {req.status}
                          </span>
                        </div>
                        <h4 className="font-bold text-base text-darkBlue-900 mb-1">
                          {req.service?.title || 'Custom Digital Solution'}
                        </h4>
                        <p className="text-xs text-gray-500 mb-3 line-clamp-2">{req.notes}</p>
                      </div>

                      <div className="pt-3 border-t border-gray-100 flex items-center justify-between text-xs text-gray-400">
                        <span>Timeline: <strong>{req.timeline || 'Standard'}</strong></span>
                        <span>Logged: {new Date(req.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  ))}

                  {subscriptions.map((sub) => (
                    <div
                      key={sub.id}
                      className="bg-white rounded-3xl p-6 border border-gray-200/80 shadow-sm flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] font-bold text-purple-700 bg-purple-50 px-2.5 py-0.5 rounded-full">
                            Recurring Retainer
                          </span>
                          <span className="text-[10px] font-bold bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full">
                            {sub.status}
                          </span>
                        </div>
                        <h4 className="font-bold text-base text-darkBlue-900 mb-1">{sub.name}</h4>
                        <p className="text-xs text-gray-500 mb-2">
                          Next Renewal Date: <strong>{new Date(sub.next_renewal_date).toLocaleDateString()}</strong>
                        </p>
                      </div>

                      <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                        <span className="text-base font-bold text-darkBlue-900">
                          ${sub.amount.toFixed(2)} / {sub.billing_interval.toLowerCase()}
                        </span>
                        <span className="text-xs font-semibold text-emerald-600">Auto-Renewal Active</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : activeTab === 'projects' ? (
            /* Tab 2: My Projects */
            projects.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h4 className="text-base font-bold text-gray-800">No active projects yet</h4>
                <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">
                  Once a quotation is accepted and manual payment is approved, your project workspace will appear here.
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {projects.map((proj) => (
                  <div
                    key={proj.id}
                    className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 sm:p-8 space-y-6"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-1.5">
                          <span className="text-xs font-mono font-bold text-gray-400">
                            {proj.project_number}
                          </span>
                          <span
                            className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                              proj.status === 'Completed'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : proj.status === 'In Progress'
                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : 'bg-amber-50 text-amber-700 border border-amber-200'
                            }`}
                          >
                            {proj.status}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">{proj.title}</h3>
                        <p className="text-xs text-gray-500 mt-1">{proj.description}</p>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <span className="text-xs text-gray-400 block mb-0.5">Project Budget</span>
                          <span className="text-xl font-bold text-darkBlue-900">
                            ${proj.budget.toFixed(2)} {proj.currency}
                          </span>
                        </div>

                        {proj.status !== 'Completed' && (
                          <button
                            onClick={() => setRevisionProject(proj)}
                            className="px-3.5 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold transition-colors"
                          >
                            Request Revision
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div>
                      <div className="flex justify-between items-center text-xs font-semibold mb-2">
                        <span className="text-gray-600">Completion Milestone</span>
                        <span className="text-clairBlue-600">{proj.progress_percent}%</span>
                      </div>
                      <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-clairBlue-500 rounded-full transition-all duration-500"
                          style={{ width: `${proj.progress_percent}%` }}
                        />
                      </div>
                    </div>

                    {/* Milestones Checklist */}
                    {proj.milestones && proj.milestones.length > 0 && (
                      <div className="pt-4 border-t border-gray-100">
                        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider mb-3">
                          Project Milestones & Deliverables
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          {proj.milestones.map((m) => (
                            <div
                              key={m.id}
                              className="p-3 bg-gray-50 rounded-xl border border-gray-200/60 text-xs flex items-center justify-between"
                            >
                              <div className="flex items-center gap-2">
                                <CheckCircle2
                                  className={`w-4 h-4 ${
                                    m.status === 'Completed' || m.status === 'Approved'
                                      ? 'text-emerald-500'
                                      : 'text-gray-300'
                                  }`}
                                />
                                <span className="font-semibold text-gray-800">{m.title}</span>
                              </div>
                              <span className="text-[10px] text-gray-400 font-medium">
                                {m.status}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : activeTab === 'quotes' ? (
            /* Tab 3: My Quotations */
            quotations.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h4 className="text-base font-bold text-gray-800">No pending quotations</h4>
                <p className="text-xs text-gray-500">Submit a service request to receive an official formal quote.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {quotations.map((quote) => (
                  <div
                    key={quote.id}
                    className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-6"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono font-bold text-gray-400">
                          {quote.quotation_number}
                        </span>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                            quote.status === 'Accepted'
                              ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                              : quote.status === 'Rejected'
                              ? 'bg-red-50 text-red-700 border border-red-200'
                              : 'bg-amber-50 text-amber-700 border border-amber-200'
                          }`}
                        >
                          {quote.status}
                        </span>
                      </div>
                      <h4 className="text-base font-bold text-gray-900">
                        Formal Service Proposal & Quotation
                      </h4>
                      <p className="text-xs text-gray-500 mt-1">
                        Valid until:{' '}
                        {quote.valid_until
                          ? new Date(quote.valid_until).toLocaleDateString()
                          : '30 days from issuance'}
                      </p>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="text-right mr-3">
                        <span className="text-xs text-gray-400 block">Total Amount</span>
                        <span className="text-xl font-bold text-darkBlue-900">
                          ${quote.total_amount.toFixed(2)} {quote.currency}
                        </span>
                      </div>

                      <button
                        onClick={() => generateQuotationPDF(quote, quote.items || [], profile)}
                        className="px-3.5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>PDF</span>
                      </button>

                      {quote.status === 'Sent' && (
                        <>
                          <button
                            onClick={() => handleAcceptQuote(quote.id)}
                            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold shadow-md transition-colors"
                          >
                            Accept Quote
                          </button>
                          <button
                            onClick={() => handleRejectQuote(quote.id)}
                            className="px-3 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl text-xs font-bold transition-colors"
                          >
                            Decline
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : activeTab === 'invoices' ? (
            /* Tab 4: My Invoices */
            invoices.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h4 className="text-base font-bold text-gray-800">No invoices issued</h4>
                <p className="text-xs text-gray-500">Invoices will appear here once commissioned work commences.</p>
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs min-w-[650px]">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase tracking-wider font-semibold">
                      <tr>
                        <th className="px-6 py-4">Invoice #</th>
                        <th className="px-6 py-4">Due Date</th>
                        <th className="px-6 py-4">Total Amount</th>
                        <th className="px-6 py-4">Balance Due</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700 font-medium">
                      {invoices.map((inv) => {
                        const balDue = Number(
                          inv.balance_due ?? (inv.total_amount - (inv.amount_paid || 0))
                        );

                        return (
                          <tr key={inv.id} className="hover:bg-gray-50/80 transition-colors">
                            <td className="px-6 py-4 font-mono font-bold text-darkBlue-900">
                              {inv.invoice_number}
                            </td>
                            <td className="px-6 py-4">{new Date(inv.due_date).toLocaleDateString()}</td>
                            <td className="px-6 py-4 font-bold text-gray-900">
                              ${inv.total_amount.toFixed(2)}
                            </td>
                            <td className="px-6 py-4 font-bold text-red-600">
                              ${balDue.toFixed(2)}
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold ${
                                  inv.status === 'Paid'
                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                    : inv.status === 'Partially Paid'
                                    ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                    : 'bg-red-50 text-red-700 border border-red-200'
                                }`}
                              >
                                {inv.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => generateInvoicePDF(inv, inv.items || [], profile)}
                                  className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold flex items-center gap-1"
                                >
                                  <Download className="w-3.5 h-3.5" />
                                  <span>PDF</span>
                                </button>

                                {balDue > 0 ? (
                                  <button
                                    onClick={() => openReceiptSubmission(inv)}
                                    className="px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold flex items-center gap-1 shadow-sm"
                                  >
                                    <Upload className="w-3.5 h-3.5" />
                                    <span>Upload Proof</span>
                                  </button>
                                ) : (
                                  <span className="text-emerald-600 text-xs font-bold flex items-center gap-1">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Settled
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )
          ) : activeTab === 'payments' ? (
            /* Tab 5: My Payments & Receipt Verification Status */
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-darkBlue-900">Manual Payment Submissions</h3>
                  <p className="text-xs text-gray-500">Track verification of uploaded receipts by our Finance Officers</p>
                </div>
                <button
                  onClick={() => openReceiptSubmission()}
                  className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm"
                >
                  <Upload className="w-4 h-4" />
                  <span>Submit Payment Receipt</span>
                </button>
              </div>

              {payments.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                  <DollarSign className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h4 className="text-base font-bold text-gray-800">No payment receipts submitted</h4>
                  <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">
                    After transferring funds via Mobile Money or Bank deposit, upload your proof of payment here.
                  </p>
                  <button
                    onClick={() => openReceiptSubmission()}
                    className="px-5 py-2.5 bg-darkBlue-900 text-white rounded-xl text-xs font-bold"
                  >
                    Submit Proof of Payment
                  </button>
                </div>
              ) : (
                <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs min-w-[650px]">
                    <thead className="bg-gray-50 border-b border-gray-200 text-gray-500 uppercase tracking-wider font-semibold">
                      <tr>
                        <th className="px-6 py-4">Reference</th>
                        <th className="px-6 py-4">Channel / Method</th>
                        <th className="px-6 py-4">Amount</th>
                        <th className="px-6 py-4">Date Submitted</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 text-gray-700 font-medium">
                      {payments.map((pay) => (
                        <tr key={pay.id} className="hover:bg-gray-50/80">
                          <td className="px-6 py-4 font-mono font-bold text-darkBlue-900">
                            {pay.payment_reference}
                            {pay.provider_tx_id && (
                              <span className="block text-[11px] text-gray-400 font-normal">
                                Tx: {pay.provider_tx_id}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">{pay.payment_method || pay.provider}</td>
                          <td className="px-6 py-4 font-bold text-darkBlue-900">
                            ${Number(pay.submitted_amount || pay.amount).toFixed(2)} {pay.currency}
                          </td>
                          <td className="px-6 py-4">{new Date(pay.created_at).toLocaleDateString()}</td>
                          <td className="px-6 py-4">
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
                                pay.status === 'Approved' || pay.status === 'Paid'
                                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                  : pay.status === 'Payment Under Review'
                                  ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                  : pay.status === 'Rejected'
                                  ? 'bg-red-50 text-red-700 border border-red-200'
                                  : 'bg-blue-50 text-blue-700 border border-blue-200'
                              }`}
                            >
                              {pay.status}
                            </span>
                            {pay.rejection_reason && (
                              <p className="text-[10px] text-red-600 mt-1 max-w-xs">
                                Note: {pay.rejection_reason}
                              </p>
                            )}
                          </td>
                          <td className="px-6 py-4 text-right">
                            {pay.receipt_url && (
                              <a
                                href={pay.receipt_url}
                                target="_blank"
                                rel="noreferrer"
                                className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-semibold inline-flex items-center gap-1"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                <span>Receipt</span>
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'courses' ? (
            /* Tab 6: My Courses (Learning Hub integration) */
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-darkBlue-900">BientoMart Academy Learning Hub</h3>
                  <p className="text-xs text-gray-500">Your enrolled courses, progress, and verifiable certificates</p>
                </div>
                <Link
                  to="/academy"
                  className="px-4 py-2 bg-darkBlue-900 text-white rounded-xl text-xs font-bold"
                >
                  Browse Academy Courses
                </Link>
              </div>

              {enrollments.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                  <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h4 className="text-base font-bold text-gray-800">No active course enrollments</h4>
                  <p className="text-xs text-gray-500 max-w-sm mx-auto mb-4">
                    Upgrade your digital skills in Web Development, AI, Computer Basics, or Digital Marketing.
                  </p>
                  <Link
                    to="/academy"
                    className="px-5 py-2.5 bg-darkBlue-900 text-white rounded-xl text-xs font-bold"
                  >
                    View Academy Catalog
                  </Link>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {enrollments.map((enr) => (
                    <div
                      key={enr.id}
                      className="bg-white rounded-3xl p-6 border border-gray-200/80 shadow-sm flex flex-col justify-between"
                    >
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[10px] font-bold bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full">
                            {enr.status}
                          </span>
                          <span className="text-xs text-gray-400">
                            Enrolled: {new Date(enr.enrolled_at).toLocaleDateString()}
                          </span>
                        </div>
                        <h4 className="font-bold text-base text-darkBlue-900 mb-1">
                          {enr.course?.title || 'Academy Course'}
                        </h4>
                        <p className="text-xs text-gray-500 line-clamp-2 mb-4">
                          {enr.course?.short_description}
                        </p>
                      </div>

                      <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                        <Link
                          to={`/academy/learn/${enr.course?.slug}`}
                          className="px-4 py-2 bg-darkBlue-900 hover:bg-darkBlue-800 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all shadow-sm"
                        >
                          <span>Go to Classroom</span>
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* Tab 7: My Support */
            tickets.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-3xl border border-gray-100 p-8 shadow-sm">
                <LifeBuoy className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <h4 className="text-base font-bold text-gray-800">No support tickets opened</h4>
                <p className="text-xs text-gray-500 mb-4">Need assistance with your website, hosting, or billing?</p>
                <button
                  onClick={() => setShowNewTicketModal(true)}
                  className="px-5 py-2.5 bg-darkBlue-600 text-white rounded-xl text-xs font-bold"
                >
                  Open a Support Ticket
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {tickets.map((tck) => (
                  <div
                    key={tck.id}
                    onClick={() => {
                      setSelectedTicketForChat(tck);
                      setShowChatModal(true);
                    }}
                    className="bg-white rounded-3xl shadow-sm border border-gray-100 p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 cursor-pointer hover:border-clairBlue-300 hover:shadow-md transition-all"
                  >
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono font-bold text-gray-400">
                          {tck.ticket_number}
                        </span>
                        <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-clairBlue-50 text-clairBlue-700 border border-clairBlue-200">
                          {tck.status}
                        </span>
                        <span className="text-xs text-amber-600 font-semibold bg-amber-50 px-2 py-0.5 rounded-md">
                          {tck.priority} Priority
                        </span>
                      </div>
                      <h4 className="text-base font-bold text-gray-900">{tck.subject}</h4>
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2">{tck.description}</p>
                    </div>

                    <div className="flex items-center gap-4 flex-shrink-0">
                      <span className="text-xs text-gray-400">
                        {new Date(tck.created_at).toLocaleDateString()}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedTicketForChat(tck);
                          setShowChatModal(true);
                        }}
                        className="px-4 py-2 bg-darkBlue-900 text-white rounded-xl text-xs font-bold flex items-center gap-1.5 shadow-sm"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        <span>Chat Support</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      </main>

      {/* Manual Receipt Modal */}
      <ManualReceiptModal
        isOpen={showReceiptModal}
        onClose={() => {
          setShowReceiptModal(false);
          setSelectedInvoiceForReceipt(null);
          setSelectedPaymentForUpdate(null);
        }}
        invoice={selectedInvoiceForReceipt}
        existingPayment={selectedPaymentForUpdate}
        onSuccess={fetchClientData}
      />

      {/* Ticket Chat Modal */}
      <TicketChatModal
        ticket={selectedTicketForChat}
        isOpen={showChatModal}
        onClose={() => {
          setShowChatModal(false);
          setSelectedTicketForChat(null);
        }}
        onTicketUpdated={fetchClientData}
      />

      {/* New Support Ticket Modal */}
      {showNewTicketModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Open Support Ticket</h3>
            <form onSubmit={handleCreateTicket} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  required
                  value={ticketSubject}
                  onChange={(e) => setTicketSubject(e.target.value)}
                  placeholder="e.g. Domain DNS configuration assistance"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Priority</label>
                <select
                  value={ticketPriority}
                  onChange={(e) => setTicketPriority(e.target.value as any)}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl"
                >
                  <option value="Low">Low - General Question</option>
                  <option value="Medium">Medium - Standard Request</option>
                  <option value="High">High - Impaired Feature</option>
                  <option value="Urgent">Urgent - Complete Downtime</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Detailed Description *</label>
                <textarea
                  rows={4}
                  required
                  value={ticketDescription}
                  onChange={(e) => setTicketDescription(e.target.value)}
                  placeholder="Describe your issue with error messages, URLs, or specific steps..."
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setShowNewTicketModal(false)}
                  className="px-4 py-2 font-semibold text-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={ticketSubmitting}
                  className="px-5 py-2.5 bg-darkBlue-900 text-white rounded-xl font-bold"
                >
                  {ticketSubmitting ? 'Submitting...' : 'Submit Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Revision Request Modal */}
      {revisionProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-8 border border-gray-100">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Submit Revision Request</h3>
            <p className="text-xs text-gray-500 mb-4">{revisionProject.title}</p>

            <form onSubmit={handleSubmitRevision} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">
                  Revision Details & Adjustments *
                </label>
                <textarea
                  rows={4}
                  required
                  value={revisionNotes}
                  onChange={(e) => setRevisionNotes(e.target.value)}
                  placeholder="Describe the adjustments needed on design, content, layout, or functionality..."
                  className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl resize-none"
                />
              </div>

              <div className="flex justify-end gap-3 pt-3 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setRevisionProject(null)}
                  className="px-4 py-2 font-semibold text-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submittingRevision}
                  className="px-5 py-2.5 bg-darkBlue-900 text-white rounded-xl font-bold"
                >
                  {submittingRevision ? 'Submitting...' : 'Send Revision Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
